/**
 * ¿Por qué NO se puede reservar este carro hoy?
 *
 * POR QUÉ EXISTE ESTO APARTE DE listing-publish-blockers.
 * Ese archivo responde otra pregunta: "¿qué le falta a este carro para poder
 * PUBLICARSE?", y por eso la pantalla de Mis vehículos lo apagaba a propósito
 * para los carros ya activos — a nadie hay que recordarle que le faltan fotos
 * cuando su carro lleva dos meses publicado.
 *
 * Pero un documento que SE VENCIÓ no es un requisito pendiente de publicación:
 * es un carro vivo que dejó de poder recibir reservas. La base lo rechaza en
 * `private.assert_vehicle_papers_are_current`, un trigger BEFORE INSERT sobre
 * `rentals` que levanta 42501 con "Este vehículo tiene un documento vencido y
 * no se puede reservar." El anfitrión nunca ve ese error: lo ve el huésped, en
 * forma de pago rechazado, y se va.
 *
 * QUÉ TAN CARO SALIÓ NO TENER ESTO. Cuatro carros de un mismo anfitrión —una
 * Hilux, una Hiace, una L200 y una NP300— llevaban desde el 1 y el 30 de junio
 * sin poder recibir una sola reserva, apareciendo como "Activo" en su pantalla,
 * mientras la app ya tenía la fecha de vencimiento cargada y no la miraba.
 *
 * ES UN ESPEJO, NO UNA SEGUNDA OPINIÓN. La regla se copia tal cual de
 * `private.vehicle_documents_are_current`:
 *   - documentos `approved`, `expired` o `rejected` con vencimiento conocido,
 *   - solo si `expiry_date` NO es nulo (hay documentos viejos cargados antes de
 *     que se extrajera la fecha; convertirlos en bloqueo retroactivo sacaría
 *     del mercado a gente que no hizo nada mal),
 *   - solo si esa fecha YA PASÓ,
 *   - sin reemplazo posterior del mismo tipo, aprobado y vigente.
 * Si la regla del servidor cambia, cambia aquí. Que el aviso diga algo distinto
 * de lo que el trigger hace es peor que no tener aviso.
 */
import type { HostVehicle } from '../types/vehicle.types'
// `todayISO` y `daysFromTodayISO` ya existen y están centralizadas justamente
// para que cada pantalla no invente su propio "hoy". No hay una segunda acá.
import { daysFromTodayISO, todayISO } from './dates'

export interface ExpiredVehicleDocument {
  /** `tarjeta_circulacion_front`, `insurance_policy`, … */
  documentType: string
  /** ISO `YYYY-MM-DD`. Nunca nulo: un documento sin fecha no llega hasta acá. */
  expiryDate: string
}

/**
 * Los documentos vencidos de un vehículo, del más viejo al más reciente — el
 * mismo orden que usa el servidor para decidir cuál nombra en el error.
 */
export function expiredVehicleDocuments(
  vehicle: Pick<HostVehicle, 'documents'>,
): ExpiredVehicleDocument[] {
  const today = todayISO()
  return vehicle.documents
    .filter(
      (doc) =>
        ['approved', 'expired', 'rejected'].includes(doc.status) &&
        typeof doc.expiryDate === 'string' &&
        doc.expiryDate.length > 0 &&
        doc.expiryDate < today &&
        !vehicle.documents.some(
          (replacement) =>
            replacement.documentType === doc.documentType &&
            replacement.status === 'approved' &&
            !!replacement.expiryDate &&
            replacement.expiryDate >= today &&
            replacement.createdAt >= doc.createdAt,
        ),
    )
    .map((doc) => ({ documentType: doc.documentType, expiryDate: doc.expiryDate as string }))
    .sort((a, b) => a.expiryDate.localeCompare(b.expiryDate))
}

/** ¿La base va a rechazar una reserva para este carro por papeles vencidos? */
export function hasExpiredVehicleDocuments(vehicle: Pick<HostVehicle, 'documents'>): boolean {
  return expiredVehicleDocuments(vehicle).length > 0
}

/**
 * Documentos que vencen PRONTO y todavía no vencen. Sirve para avisar antes de
 * que el carro se caiga del mercado, no después: el aviso útil llega mientras
 * todavía se puede reservar.
 */
export function expiringVehicleDocuments(
  vehicle: Pick<HostVehicle, 'documents'>,
  withinDays = 30,
): ExpiredVehicleDocument[] {
  const today = todayISO()
  const limitISO = daysFromTodayISO(withinDays)

  return vehicle.documents
    .filter(
      (doc) =>
        doc.status === 'approved' &&
        typeof doc.expiryDate === 'string' &&
        doc.expiryDate.length > 0 &&
        doc.expiryDate >= today &&
        doc.expiryDate <= limitISO,
    )
    .map((doc) => ({ documentType: doc.documentType, expiryDate: doc.expiryDate as string }))
    .sort((a, b) => a.expiryDate.localeCompare(b.expiryDate))
}
